import { Modal } from "antd";
import { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes } from "react-router";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { bindActionCreators } from "redux";
import { showMessege } from "../components/CustomMessage";
import { ActionCreators } from "../store";
import { BookingApi, SettingsApi } from "../store/api";
import googleTagFire from "../utills/googleTagFire";

const ZoomJoinSession = lazy(() => import("../pages/zoom/ZoomJoinSession"));
const ChatOnline = lazy(
  () => import("../pages/MatchingQuestionaries/ChatOnline"),
);
const PhoneCall = lazy(
  () => import("../pages/MatchingQuestionaries/PhoneCall"),
);
const MyJourney = lazy(() => import("../pages/MyJourney"));
const MbcQuestionaire = lazy(() => import("../pages/MbcQuestionaire"));
const AccountDelete = lazy(() => import("../pages/DeleteAccount"));
const Loader = lazy(() => import("../components/Loader"));
const ReduceRate = lazy(() => import("../pages/ReduceRate"));
const AccountSetup = lazy(() => import("../pages/AccountSetup"));

const AppointmentOverview = lazy(() => import("../pages/AppointmentOverview"));
const BrowseTherapist = lazy(() => import("../pages/BrowseTherapist"));

const MatchingQuestionnaire = lazy(
  () => import("../pages/MatchingQuestionaries"),
);
const MatchingTools = lazy(() => import("../pages/MatchingTools"));
const RecommendedMatches = lazy(() => import("../pages/RecommendedMatches"));
const SettingsScreen = lazy(() => import("../pages/SettingsV2"));

const TherapistProfile = lazy(
  () => import("../pages/TherapistDetail/TherapistProfile"),
);
const SelectDateTime = lazy(
  () => import("../pages/SelectDateTime/SelectDateTimePage"),
);
const SelectSessionDuration = lazy(
  () => import("../pages/SelectSessionDuration/SelectBookingDurationPage"),
);
const ConfirmBooking = lazy(
  () => import("../pages/ConfirmBooking/ConfirmBookingPage"),
);
const AppointmentConfirmation = lazy(
  () => import("../pages/AppointmentConfirmation"),
);
const SelectStudio = lazy(() => import("../pages/SelectStudio"));
const IntakeForm = lazy(
  () => import("../pages/Settings/Forms/intakeForm/IntakeForm"),
);

const SignIn = lazy(() => import("../pages/SignIn"));
const SignUp = lazy(() => import("../pages/SignUp"));
const TherapyTab = lazy(() => import("../pages/TherapyTab"));
const DirectBilling = lazy(
  () => import("../pages/Settings/Billing/DirectBilling"),
);
const MandatoryFields = lazy(() => import("../components/MandatoryFields"));
const CustomAuth = lazy(() => import("../pages/CustomAuth/CustomAuth"));

export default function NavigationRoutes() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPage = location.pathname;
  const uniqueId = searchParams.get("ID");
  const loginStatus = useSelector((state) => state.AuthReducer.loginStatus); //store data
  const gotoReduceRate = useSelector(
    (state) => state.AuthReducer.gotoReduceRate,
  );
  const storeUniqueId = useSelector(
    (state) => state.ConfirmBookingDataReducer?.uniqueId,
  );
  const therapyReducer = useSelector((state) => state.TherapyReducer);
  const authReducer = useSelector((state) => state.AuthReducer);
  const { setUniqueId, setConfirmBookingDataAction } = bindActionCreators(
    ActionCreators,
    dispatch,
  );
  const [showMandatoryModal, setshowMandatoryModal] = useState(false);
  const [loading, setLoading] = useState(true);
  // null = not checked yet, true/false once the user_instructors call resolves.
  const [hasInstructorHistory, setHasInstructorHistory] = useState(null);

  useEffect(() => {
    if (!authReducer?.loginLoader) {
      setLoading(false);
    }
    if (
      loginStatus &&
      currentPage !== "/account-setup" &&
      !authReducer?.loginLoader
    ) {
      const isIncompleteProfile =
        authReducer?.userInfo?.first_name === "" ||
        authReducer?.userInfo?.last_name === "" ||
        authReducer?.userInfo?.date_of_birth === null;
      setshowMandatoryModal(isIncompleteProfile);
    } else {
      setshowMandatoryModal(false);
    }
  }, [
    loginStatus,
    currentPage,
    authReducer?.userInfo,
    authReducer?.loginLoader,
  ]);

  useEffect(() => {
    googleTagFire("Page View", "Route", location?.pathname);
    const isValidUniqueId =
      uniqueId !== null && uniqueId !== undefined && uniqueId !== "";
    if (location.pathname === "/waitlist" && isValidUniqueId) {
      fetchGoToDateTimeRoute(uniqueId);
    }
  }, [location.pathname, uniqueId]);

  // Redirect root path to /matching if not logged in or no therapist
  useEffect(() => {
    // Wait for the user_instructors call to resolve before deciding to bounce
    // the user off "/", to avoid a premature redirect while it's in flight.
    if (location.pathname === "/" && !loading && hasInstructorHistory !== null) {
      const instructorId = window.localStorage.getItem("instructor_id");
      const hasTherapist =
        loginStatus &&
        ((instructorId && instructorId !== "undefined") ||
          therapyReducer?.result?.patient_profile?.instructor ||
          therapyReducer?.result?.appointment ||
          hasInstructorHistory);

      if (!hasTherapist) {
        navigate("/matching", { replace: true });
      }
    }
  }, [
    location.pathname,
    loginStatus,
    therapyReducer,
    loading,
    hasInstructorHistory,
    navigate,
  ]);

  useEffect(() => {
    const isValidStoreUniqueId =
      storeUniqueId !== null &&
      storeUniqueId !== undefined &&
      storeUniqueId !== "";
    if (isValidStoreUniqueId && loginStatus) {
      fetchGoToDateTimeRoute(storeUniqueId);
    }
  }, [storeUniqueId, loginStatus]);

  useEffect(() => {
    if (loginStatus) {
      dispatch(ActionCreators.getTherapyIndex());
      localStorage.setItem("BlockReload", "true");
      const submissionId = window.localStorage.getItem("submission_id");
      if (submissionId) {
        window.localStorage.removeItem("submission_id");
      }

      // Called here (not just inside TherapyTab) because a fresh account with
      // no patient_profile.instructor/appointment yet never mounts TherapyTab,
      // so its internal user_instructors call would never fire otherwise.
      SettingsApi.getUserInstructors()
        .then((res) => {
          setHasInstructorHistory(res?.data?.results?.length > 0);
        })
        .catch(() => {
          setHasInstructorHistory(false);
        });
    } else {
      setHasInstructorHistory(null);
    }
  }, [loginStatus]);

  const fetchGoToDateTimeRoute = async (id) => {
    if (loginStatus) {
      const res = await BookingApi.getSelectDateTimeData(id);
      if (res?.status === 200) {
        setTimeout(() => {
          setConfirmBookingData(res);
        }, 500);
      } else {
        setUniqueId(null);
        showMessege(
          "error",
          res?.data?.details ?? "Could not validate credentials!",
        );
        setLoading(false);
      }
    } else {
      showMessege("error", "Please Login to book the Appointment!");
      setUniqueId(id);
      navigate("/sign-in");
      setLoading(false);
    }
  };

  const setConfirmBookingData = (res) => {
    let data = {
      instructor: res?.data?.instructor?.id,
      instructor_name:
        res?.data?.instructor?.first_name +
        " " +
        res?.data?.instructor?.last_name,
      instructor_pronoun: res?.data?.instructor?.instructor?.pronoun,
      instructor_designation: res?.data?.instructor?.instructor?.designation,
      instructor_profile_photo: res?.data?.instructor?.instructor?.image_path,
      start_at: null,
      available_at: res?.data?.available_at,

      appointment_type: {
        name: res?.data?.appointment_type?.appointment_type?.name,
        label: res?.data?.appointment_type?.appointment_type?.label,
        key: res?.data?.appointment_type?.appointment_type?.key,
      },
      appointment_location: {
        name: res?.data?.appointment_type?.appointment_location?.name,
        label: res?.data?.appointment_type?.appointment_location?.label,
        key: res?.data?.appointment_type?.appointment_location?.key,
      },
      appointment_age_category: {
        name: res?.data?.appointment_type?.appointment_age_category?.name,
        label: res?.data?.appointment_type?.appointment_age_category?.label,
        key: res?.data?.appointment_type?.appointment_age_category?.key,
      },
      appointment_duration: {
        name: res?.data?.appointment_type?.appointment_duration?.name,
        label: res?.data?.appointment_type?.appointment_duration?.label,
        key: res?.data?.appointment_type?.appointment_duration?.key,
      },
      studio_location: res?.data?.studio_location,
      appointment_type_id: res?.data?.appointment_type?.id,
    };
    setConfirmBookingDataAction(data);
    navigate("/select-date-time", { replace: true });
    setLoading(false);
  };

  const getInitialRoute = () => {
    const submission = JSON.parse(localStorage.getItem("user_form_submission"));
    const instructorId = window.localStorage.getItem("instructor_id");
    const pathname = location.pathname;
    if (pathname === "/account-setup") {
      return <AccountSetup />;
    } else if (gotoReduceRate) {
      return <ReduceRate />;
    } else if (pathname === "/custom-auth") {
      return <CustomAuth />;
    } else if (submission?.submission_type === "PHONE_CALL") {
      return <PhoneCall />;
    } else if (submission?.submission_type === "CHAT_ONLINE") {
      return <ChatOnline />;
    } else if (
      window.localStorage.getItem("submission_id") ||
      therapyReducer?.result?.recommended_instructors?.length > 0 ||
      ["THERAPY_AS_A_SERVICE", "ASSESSMENT_AS_A_SERVICE"].includes(
        submission?.submission_type,
      )
    ) {
      return <RecommendedMatches />;
    } else if (
      loginStatus &&
      ((instructorId && instructorId !== "undefined") ||
        therapyReducer?.result?.patient_profile?.instructor ||
        therapyReducer?.result?.appointment ||
        hasInstructorHistory)
    ) {
      return <TherapyTab />;
    } else {
      return <MatchingTools />;
    }
  };

  if (loading) {
    return (
      <div style={{ height: "88vh", width: "100%" }}>
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={getInitialRoute()} />
          <Route path="/matching" element={<MatchingTools />} />
          <Route path="/matching-tools" element={<MatchingTools />} />
          <Route path="*" element={getInitialRoute()} />
          <Route path="/phone-call" element={<PhoneCall />} />
          <Route path="/chat-online" element={<ChatOnline />} />
          <Route path="/select-studio" element={<SelectStudio />} />
          <Route path="/recommended-list" element={<RecommendedMatches />} />
          <Route path="/browse-therapist/:id" element={<BrowseTherapist />} />
          <Route
            path="/matching-questionnaire"
            element={<MatchingQuestionnaire />}
          />
          <Route path="/therapist-detail/:id" element={<TherapistProfile />} />

          {/* <Route path="/request-location" element={<RequestLocation />} /> */}

          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />
          {/* <Route path="/therapy" element={<TherapyTab />} />
          <Route path="/reduced-rate" element={<ReduceRate />} />
          <Route path="/delete-account" element={<AccountDelete />} /> */}
          {loginStatus && (
            <>
              <Route path="/therapy" element={<TherapyTab />} />
              <Route path="/reduced-rate" element={<ReduceRate />} />
              <Route
                path="/appointment-confirmation"
                element={<AppointmentConfirmation />}
              />
              <Route
                path="/appointment-confirmation-first"
                element={<AppointmentConfirmation />}
              />
              <Route path="/delete-account" element={<AccountDelete />} />
              <Route
                path="/select-session-duration"
                element={<SelectSessionDuration />}
              />
              <Route path="/select-date-time" element={<SelectDateTime />} />
              <Route path="/confirm-appointment" element={<ConfirmBooking />} />

              <Route path="/account-setup" element={<AccountSetup />} />
              <Route
                path="/appointment-overview"
                element={<AppointmentOverview />}
              />
              <Route path="/settings" element={<SettingsScreen />} />
              {/*<Route path="/settingsv2" element={<SettingsV2 />} />*/}
              <Route
                path="/settings/insurance-and-direct-billing/:id"
                element={<DirectBilling />}
              />
              <Route path="/form-details" element={<IntakeForm />} />
              <Route path="/custom-auth" element={<CustomAuth />} />
              <Route path="/my-journey" element={<MyJourney />} />
              <Route path="/mbc-questionaire" element={<MbcQuestionaire />} />
            </>
          )}
          <Route path="/join-zoom/:id" element={<ZoomJoinSession />} />
        </Routes>
      </Suspense>
      <Modal
        open={showMandatoryModal}
        centered={true}
        title={"Please Provide Following Information(s)."}
        closable={false}
        width={600}
        onCancel={() => {}}
        footer={<></>}
        destroyOnClose
      >
        <MandatoryFields />
      </Modal>
    </>
  );
}
